import { MATCH_STATE } from '../machine';
import { ok, reject } from '../result';
import type {
  AnnotationOp,
  Decision,
  DeleteAnnotationCommand,
  DeleteAnnotationSnapshot,
  WriteAnnotationCommand,
  WriteAnnotationSnapshot,
} from './types';

/**
 * B spec §4.2, scenario 17 — **hypothesis annotations** (build unit B-2): the per-pattern
 * free-text notes the detail sheet captures at a dead spell and the Coach Claude payload reads
 * later (§5.2). Never required during play — LIVE-9's "no in-play flow may *require* free-text
 * entry" is the UI's obligation; this module's is that an annotation can never do anything but
 * be text on a pattern ({@link AnnotationOp} is a one-table extract).
 *
 * ── Typing is allowed here, and that is the standing ruling ──────────────────
 *
 * The same ruling `commands/halftimeManage.ts` carries for the HT-2 sheet: LIVE-9 forbids
 * *requiring* typing in play, not offering it. The detail sheet is reachable mid-half and its
 * Annotate field is optional there; nothing in this window says otherwise.
 */

const MODULE = 'commands/annotations';

/**
 * B §4.2's window — `firstHalf → distill`, as a set rather than a chain of `!==`.
 *
 * The two states left out are the whole of the negative: `preMatch` (nothing has been observed,
 * so there is nothing to annotate) and `archived` (the record is closed; `CompleteMatch`'s F-1
 * guard resolves drafts *before* archiving for exactly this reason — nothing is editable after).
 *
 * **This set coincides with `commands/flags.ts`'s `RESOLVABLE_IN` and deliberately does not
 * share it.** The two windows are two independent rulings — FLAG-3/DIST-1's review window and
 * spec §4.2's annotation window — that happen to name the same six states today. Sharing one
 * symbol would couple a future re-ruling of either to the other (the {@link HalftimeAddOp}
 * argument for same-shape-different-reason types), so each names its own. The B-20 duplication
 * ledger carries this as documented coincidence.
 */
const WRITABLE_IN: ReadonlySet<string> = new Set<string>([
  MATCH_STATE.firstHalf,
  MATCH_STATE.halftime,
  MATCH_STATE.secondHalf,
  MATCH_STATE.fullTime,
  MATCH_STATE.triage,
  MATCH_STATE.distill,
]);

/**
 * B §4.2 — create or edit one annotation, decided against the in-transaction read.
 *
 * ── The order of the guards is the order of the questions ────────────────────
 *
 * Match, then window, then *does the row make sense*, then *is there anything in it*, then
 * insert-vs-edit. Each is a different sentence to the user, each read from the snapshot the
 * transaction opened with — the `handleResolveFlag` discipline.
 *
 * The hypothesis existence check binds the **insert arm only**: an existing annotation's
 * hypothesis was checked when it was created, and both rows leave together under the INV-8
 * cascade, so on the edit arm the question has already been answered by the row's survival.
 */
export function handleWriteAnnotation(
  cmd: WriteAnnotationCommand,
  snap: WriteAnnotationSnapshot,
): Decision<AnnotationOp> {
  const match = snap.match;
  if (match === undefined) {
    return reject(MODULE, cmd.matchId, 'ERR_MATCH_NOT_FOUND', 'That match no longer exists.');
  }

  if (!WRITABLE_IN.has(match.state)) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_ANNOTATIONS_NOT_WRITABLE',
      match.state === MATCH_STATE.preMatch
        ? 'Nothing has been observed yet — annotations start with the first half.'
        : 'This match is archived; its record can no longer be annotated.',
      { state: match.state },
    );
  }

  const { row } = cmd;

  if (row.matchId !== cmd.matchId) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_COMMAND_ROW_MISMATCH',
      'That annotation could not be saved because it names a different match.',
      { id: row.id, rowMatchId: row.matchId },
    );
  }

  if (row.text.trim() === '') {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_ANNOTATION_EMPTY',
      'Write something first — an empty annotation saves nothing.',
      { id: row.id, hypothesisId: row.hypothesisId },
    );
  }

  if (snap.existing !== undefined) {
    // INV-14's edit arm. The id crossed a UI boundary, so the stored row's provenance is
    // re-checked rather than trusted: an id colliding across matches, or a sheet trying to
    // re-home a note onto another pattern, is malformed input — not an edit.
    if (
      snap.existing.matchId !== cmd.matchId ||
      snap.existing.hypothesisId !== row.hypothesisId
    ) {
      return reject(
        MODULE,
        cmd.matchId,
        'ERR_COMMAND_ROW_MISMATCH',
        'That annotation could not be saved because it belongs to a different pattern.',
        {
          id: row.id,
          storedMatchId: snap.existing.matchId,
          storedHypothesisId: snap.existing.hypothesisId,
        },
      );
    }
    return ok({
      ops: [{ t: 'updateAnnotation', id: row.id, changes: { text: row.text } }],
      log: [
        {
          module: MODULE,
          level: 'info',
          matchId: cmd.matchId,
          msg: 'annotation updated',
          data: { id: row.id, hypothesisId: row.hypothesisId },
        },
      ],
      post: [],
    });
  }

  // Insert arm — scenario 17 attaches the note to a pattern, so the pattern must be this
  // match's. A foreign match's hypothesis gets the same sentence as a vanished one: naming the
  // difference would leak another match's data into this one's error.
  if (snap.hypothesis === undefined || snap.hypothesis.matchId !== cmd.matchId) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_HYPOTHESIS_NOT_FOUND',
      'That pattern is no longer on this match, so the annotation has nothing to attach to.',
      { id: row.id, hypothesisId: row.hypothesisId },
    );
  }

  return ok({
    ops: [{ t: 'insertAnnotation', row }],
    log: [
      {
        module: MODULE,
        level: 'info',
        matchId: cmd.matchId,
        msg: 'annotation written',
        data: { id: row.id, hypothesisId: row.hypothesisId },
      },
    ],
    post: [],
  });
}

/**
 * B §4.2 — remove one annotation. The window binds a delete exactly as it binds a write — an
 * archived record loses commentary as illegally as it gains it.
 */
export function handleDeleteAnnotation(
  cmd: DeleteAnnotationCommand,
  snap: DeleteAnnotationSnapshot,
): Decision<AnnotationOp> {
  const match = snap.match;
  if (match === undefined) {
    return reject(MODULE, cmd.matchId, 'ERR_MATCH_NOT_FOUND', 'That match no longer exists.');
  }

  if (!WRITABLE_IN.has(match.state)) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_ANNOTATIONS_NOT_WRITABLE',
      match.state === MATCH_STATE.preMatch
        ? 'Nothing has been observed yet — annotations start with the first half.'
        : 'This match is archived; its record can no longer be annotated.',
      { state: match.state },
    );
  }

  // One sentence for both absences (missing, or another match's row): "not found on this match"
  // is true either way, and naming a foreign row's existence would leak it.
  if (snap.existing === undefined || snap.existing.matchId !== cmd.matchId) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_ANNOTATION_NOT_FOUND',
      'That annotation is no longer on this match.',
      { id: cmd.annotationId },
    );
  }

  return ok({
    ops: [{ t: 'deleteAnnotation', id: cmd.annotationId }],
    log: [
      {
        module: MODULE,
        level: 'info',
        matchId: cmd.matchId,
        msg: 'annotation deleted',
        data: { id: cmd.annotationId, hypothesisId: snap.existing.hypothesisId },
      },
    ],
    post: [],
  });
}
